import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowLeft, Loader2, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { invoiceApiService } from '../services/invoiceApi';
import { branchApiService } from '../services/branchApi';
import { customerApiService } from '../services/customerApi';
import { useBranch } from '../contexts/BranchContext';
import { InvoiceEwayBillLayout } from './InvoiceEwayBillLayout';
import type { InvoiceHeader } from '../services/invoiceApi';
import type { Branch } from '../services/branchApi';
import type { Customer } from '../services/customerApi';

const InvoiceEwayBillPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { selectedBranch } = useBranch();

  const [invoice, setInvoice] = useState<InvoiceHeader | null>(null);
  const [branchDetails, setBranchDetails] = useState<Branch | null>(null);
  const [shipToCustomer, setShipToCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [invRes, branchRes] = await Promise.all([
        invoiceApiService.getInvoiceHeaderById(id),
        selectedBranch ? branchApiService.getBranchById(selectedBranch.toString()) : Promise.resolve(null),
      ]);
      if (!invRes.success || !invRes.data) {
        toast.error('Invoice not found');
        setInvoice(null);
        return;
      }
      const inv = invRes.data;
      setInvoice(inv);
      setBranchDetails(branchRes ?? null);

      const shipToId = inv.shipTo ?? inv.billTo;
      const billToId = inv.billTo;
      const [shipRes, billRes] = await Promise.all([
        shipToId ? customerApiService.getCustomerById(shipToId.toString()) : Promise.resolve({ success: false, data: null }),
        billToId ? customerApiService.getCustomerById(billToId.toString()) : Promise.resolve({ success: false, data: null }),
      ]);
      if (shipRes.success && shipRes.data) setShipToCustomer(shipRes.data);
      else if (billRes.success && billRes.data) setShipToCustomer(billRes.data);
    } catch {
      toast.error('Failed to load invoice');
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  }, [id, selectedBranch]);

  useEffect(() => {
    reload();
  }, [reload]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading e-Way Bill…</p>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="container mx-auto p-6">
        <Button variant="outline" onClick={() => navigate('/invoices')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <p className="mt-4 text-gray-600">Invoice not found.</p>
      </div>
    );
  }

  const isEInvoiced = String(invoice.status) === 'E_INVOICED';
  const hasEway = !!(invoice.ewayBillNo && String(invoice.ewayBillNo).trim());

  if (!isEInvoiced || !hasEway) {
    return (
      <div className="container mx-auto p-6 max-w-lg">
        <Button variant="outline" onClick={() => navigate(`/invoices/${id}/view`)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to invoice
        </Button>
        <p className="mt-6 text-gray-700">
          e-Way Bill is available only when the invoice is e-invoiced and has an e-Way Bill number.
        </p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-5xl print:max-w-none print:p-2 print:mx-0">
      <div className="flex items-center justify-between mb-6 print:hidden">
        <Button variant="outline" onClick={() => navigate(`/invoices/${id}/view`)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to invoice
        </Button>
        <Button onClick={() => window.print()} className="bg-primary-600 hover:bg-primary-700 text-white">
          <Printer className="h-4 w-4 mr-2" />
          Print
        </Button>
      </div>

      <InvoiceEwayBillLayout
        invoice={invoice}
        branchDetails={branchDetails}
        shipToCustomer={shipToCustomer}
      />
    </div>
  );
};

export default InvoiceEwayBillPage;
